import { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { VoiceProvider } from '../context/VoiceContext';
import { api } from '../api';

/** Short chime for a newly-arrived message. Uses the Web Audio API so there's no
 *  asset to bundle; silently no-ops if the browser blocks it. */
function playNewMessageChime() {
  try {
    const Ctx: typeof AudioContext | undefined = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const beep = (freq: number, start: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + 0.22);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + 0.24);
    };
    beep(660, 0);
    beep(880, 0.18);
    setTimeout(() => ctx.close().catch(() => {}), 700);
  } catch {
    /* ignore */
  }
}

const LOGO_URL = '/logo.svg';
const SIDEBAR_EXPANDED = 240;
const SIDEBAR_COLLAPSED = 64;
const MOBILE_BREAKPOINT = 768;

export function Layout() {
  const { user, signOut } = useAuth();
  const [collapsed, setCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [pendingCount, setPendingCount] = useState(0);
  const [assistantPending, setAssistantPending] = useState(0);
  const [draftQuotes, setDraftQuotes] = useState(0);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Fetch pending supply requests count (admin only)
  useEffect(() => {
    if (user?.role !== 'ADMIN') return;
    const load = () =>
      api
        .getSupplyRequests({ status: 'pending' })
        .then((d) => setPendingCount(d.total || 0))
        .catch(() => {});
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [user?.role]);

  // Billing nav badge = number of DRAFT quotes still to send (admin only).
  useEffect(() => {
    if (user?.role !== 'ADMIN') return;
    const load = () =>
      api
        .draftQuoteCount()
        .then((d) => setDraftQuotes(d.count || 0))
        .catch(() => {});
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [user?.role]);

  // Billing nav badge (2nd pill) = number of NEW website Quote Tool
  // submissions awaiting review — distinct from draftQuotes above (DRAFT
  // Xero quotes still to send). Deliberately NOT combined into one number:
  // these are two different kinds of pending work. Layout stays mounted for
  // the whole admin app (Outlet swaps underneath it), so this is the one
  // place a query needs to live for the badge to update while the admin is
  // on any other page, not just Billing. Sourced from the category-filtered,
  // fully-paginated, website-source-only endpoint Billing.tsx itself now
  // uses for its "New Quote Requests" panel (source:'website' → gmail_id IS
  // NULL, excluding Gmail-scanned quote_request emails, which share the same
  // category) — no 200-row ceiling, unlike the legacy shared query
  // Assistant.tsx still uses for its own, unrelated 'new'-badge/list.
  // pageSize:1 because only `total` is read here.
  const { data: quoteRequestsForBadge } = useQuery({
    queryKey: ['assistant-observations', 'quote_request', 'website', 'new', 1, 1],
    queryFn: () => api.listAssistantObservationsPaginated({ status: 'new', category: 'quote_request', source: 'website', page: 1, pageSize: 1 }),
    enabled: user?.role === 'ADMIN',
    refetchInterval: 30_000,
    staleTime: 0,
    refetchIntervalInBackground: true,
  });
  const quoteRequestPending = quoteRequestsForBadge?.total ?? 0;
  // Website Contact-page "Quick Message" submissions awaiting review
  // (Contacts → Online). Sourced from the same category-filtered, fully-
  // paginated endpoint Contacts.tsx itself uses (no 200-row ceiling —
  // see assistant.ts#listObservationsPaginated) rather than the shared
  // legacy query above, so this badge stays accurate even once total 'new'
  // observations across all categories exceed 200. pageSize:1 because only
  // `total` is read here — no need to fetch 15 full rows just for a count.
  // Query key intentionally differs from Contacts.tsx's own page-N request;
  // the two happen to share a cache entry whenever Contacts is on page 1
  // (React Query dedupes identical keys automatically) and each stays cheap
  // (min(pageSize,total) rows) the rest of the time.
  const { data: onlineMessagesForBadge } = useQuery({
    queryKey: ['assistant-observations', 'quick_message', 'new', 1, 1],
    queryFn: () => api.listAssistantObservationsPaginated({ status: 'new', category: 'quick_message', page: 1, pageSize: 1 }),
    enabled: user?.role === 'ADMIN',
    refetchInterval: 30_000,
    staleTime: 0,
    refetchIntervalInBackground: true,
  });
  const onlineMessagePending = onlineMessagesForBadge?.total ?? 0;

  // Nav badge = number of conversations with unread received SMS (admin only),
  // and chime when it goes up (a new message has arrived).
  const prevPending = useRef<number | null>(null);
  useEffect(() => {
    if (user?.role !== 'ADMIN') return;
    const load = () =>
      api
        .listSmsThreads()
        .then((d) => {
          const total = (d.threads ?? []).filter((t: any) => (t.unread ?? 0) > 0).length;
          if (prevPending.current !== null && total > prevPending.current) playNewMessageChime();
          prevPending.current = total;
          setAssistantPending(total);
        })
        .catch(() => {});
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [user?.role]);

  // Close drawer when switching to desktop
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const closeMobile = () => setMobileOpen(false);

  // Mobile drawer always shows labels (expanded)
  const showLabels = isMobile ? true : !collapsed;
  const w = isMobile ? SIDEBAR_EXPANDED : collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  return (
    <VoiceProvider>
    <div
      className="layout"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      {/* ── Mobile top bar ───────────────────────────────────────── */}
      <header className="admin-topbar">
        <button
          className="admin-hamburger"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle navigation"
        >
          {mobileOpen ? '✕' : '☰'}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={LOGO_URL} alt="Bling Bling Brisbane" style={{ height: 28 }} />
          <span
            style={{
              fontFamily: 'var(--font-family-heading)',
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: 'var(--color-accent)',
            }}
          >
            ADMIN
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              backgroundColor: 'var(--color-accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1e1e1e',
              fontWeight: 700,
              fontSize: 13,
              fontFamily: 'var(--font-family-heading)',
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        </div>
      </header>

      {/* ── Backdrop overlay (mobile) ─────────────────────────────── */}
      {mobileOpen && <div className="admin-overlay" onClick={closeMobile} />}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* ── Sidebar wrapper ───────────────────────────────────────── */}
        <div className="admin-sidebar-wrapper" style={{ position: 'relative', flexShrink: 0 }}>
          <aside
            className={`admin-sidebar${mobileOpen ? ' mobile-open' : ''}`}
            style={{
              width: w,
              height: '100vh',
              position: 'sticky',
              top: 0,
              backgroundColor: '#ffffff',
              borderRight: '1px solid var(--color-border)',
              padding: showLabels ? '20px 16px' : '20px 8px',
              display: 'flex',
              flexDirection: 'column',
              transition:
                'width 0.25s cubic-bezier(.4,0,.2,1), padding 0.25s cubic-bezier(.4,0,.2,1)',
              overflow: 'hidden',
            }}
          >
            {/* Logo */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 24,
                paddingBottom: showLabels ? 16 : 12,
                borderBottom: '1px solid var(--color-border)',
                minHeight: showLabels ? 64 : 40,
              }}
            >
              {!showLabels ? (
                <span
                  style={{
                    fontFamily: 'var(--font-family-heading)',
                    fontWeight: 700,
                    fontSize: 20,
                    color: 'var(--color-text)',
                    letterSpacing: '0.04em',
                  }}
                >
                  B
                </span>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={LOGO_URL}
                    alt="Bling Bling Brisbane"
                    style={{ height: 40, display: 'block', margin: '0 auto 6px' }}
                  />
                  <span
                    style={{
                      fontSize: 10,
                      color: 'var(--color-accent)',
                      fontFamily: 'var(--font-family-heading)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.18em',
                      fontWeight: 600,
                    }}
                  >
                    Admin Portal
                  </span>
                </div>
              )}
            </div>

            {/* Nav */}
            <nav style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {user?.role === 'ADMIN' && (
                  <NavItem
                    to="/contacts"
                    icon="👥"
                    collapsed={!showLabels}
                    onNavigate={closeMobile}
                    badge={assistantPending}
                    badgeTitle="Unread text messages"
                    badge2={onlineMessagePending}
                    badge2Title="New website Quick Messages awaiting review"
                  >
                    Contacts
                  </NavItem>
                )}
                <NavItem to="/calendar" icon="📅" collapsed={!showLabels} onNavigate={closeMobile}>
                  Calendar
                </NavItem>
                <NavItem to="/availability" icon="🗓" collapsed={!showLabels} onNavigate={closeMobile}>
                  Availability
                </NavItem>
                <NavItem to="/request-supplies" icon="📦" collapsed={!showLabels} onNavigate={closeMobile}>
                  Request Supplies
                </NavItem>
                {user?.role === 'ADMIN' && (
                  <>
                    <NavItem
                      to="/supplies"
                      icon="🧰"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                      badge={pendingCount}
                    >
                      Supplies
                    </NavItem>
                    <NavItem
                      to="/billing"
                      icon="🧾"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                      badge={draftQuotes}
                      badgeTitle="Draft quotes still to send"
                      badge2={quoteRequestPending}
                      badge2Title="New website quote requests awaiting review"
                    >
                      Billing
                    </NavItem>
                    <NavItem
                      to="/reports"
                      icon="📊"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Reports
                    </NavItem>
                    <NavItem
                      to="/analytics"
                      icon="📈"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Analytics
                    </NavItem>
                    <NavItem
                      to="/knowledge-base"
                      icon="📚"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Knowledge Base
                    </NavItem>
                    <NavItem
                      to="/referrals"
                      icon="🤝"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Referrals
                    </NavItem>
                    <NavItem
                      to="/messaging"
                      icon="✉️"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Messaging
                    </NavItem>
                    <NavItem
                      to="/services"
                      icon="🧽"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Services
                    </NavItem>
                    <NavItem
                      to="/expenses"
                      icon="💵"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Expenses
                    </NavItem>
                    <NavItem
                      to="/holidays"
                      icon="🏖️"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Holidays
                    </NavItem>
                    <NavItem
                      to="/service-log"
                      icon="🔧"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Service Log
                    </NavItem>
                  </>
                )}
              </ul>
            </nav>

            {/* User section */}
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: showLabels ? 'flex-start' : 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: 'var(--font-family-heading)',
                  }}
                >
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                {showLabels && (
                  <div style={{ overflow: 'hidden' }}>
                    <p
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'var(--color-text)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user?.name}
                    </p>
                    <p
                      style={{
                        fontSize: 10,
                        color: 'var(--color-accent)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        fontWeight: 600,
                      }}
                    >
                      {user?.role}
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={signOut}
                title="Sign Out"
                className="sidebar-signout"
                style={{
                  width: '100%',
                  padding: showLabels ? '6px 12px' : '6px',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  color: 'var(--color-text-muted)',
                  fontSize: 12,
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, color 0.15s',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-accent)';
                  e.currentTarget.style.color = 'var(--color-accent)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                }}
              >
                {showLabels ? 'Sign Out' : '↩'}
              </button>
            </div>
          </aside>

          {/* Edge toggle — desktop only */}
          <button
            className="sidebar-toggle-btn"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              position: 'absolute',
              top: 28,
              right: -12,
              zIndex: 10,
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: '1px solid var(--color-border)',
              backgroundColor: '#ffffff',
              color: 'var(--color-text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 12,
              lineHeight: 1,
              padding: 0,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.borderColor = 'var(--color-accent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = 'var(--color-text-muted)';
              e.currentTarget.style.borderColor = 'var(--color-border)';
            }}
          >
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        {/* Main Content */}
        <main
          className="admin-main-content"
          style={{
            flex: 1,
            padding: 'var(--spacing-8)',
            overflow: 'auto',
            backgroundColor: 'var(--color-background)',
            minWidth: 0,
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
    </VoiceProvider>
  );
}

function NavItem({
  to,
  icon,
  children,
  collapsed,
  onNavigate,
  badge,
  badgeTitle,
  badge2,
  badge2Title,
}: {
  to: string;
  icon: string;
  children: React.ReactNode;
  collapsed: boolean;
  onNavigate?: () => void;
  /** Red pill — existing convention (e.g. draft quotes to send). */
  badge?: number;
  badgeTitle?: string;
  /** Second, distinctly-colored pill for an unrelated count that shouldn't
   *  be summed into `badge` (e.g. new website quote requests vs. draft
   *  quotes — two different kinds of pending work). */
  badge2?: number;
  badge2Title?: string;
}) {
  // Only one pill fits in the collapsed icon's corner — badge2 wins when
  // both are present since it's the more time-sensitive of the two here.
  const cornerBadge = badge2 && badge2 > 0 ? badge2 : badge;
  const cornerColor = badge2 && badge2 > 0 ? 'var(--color-accent)' : '#e53e3e';
  return (
    <li>
      <NavLink
        to={to}
        onClick={onNavigate}
        title={collapsed ? (typeof children === 'string' ? children : '') : undefined}
        style={({ isActive }) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: collapsed ? 0 : 10,
          padding: collapsed ? '10px 0' : '8px 12px',
          borderRadius: 6,
          backgroundColor: isActive ? 'var(--color-accent)' : 'transparent',
          color: isActive ? '#1e1e1e' : 'var(--color-text)',
          textDecoration: 'none',
          fontSize: 13,
          fontWeight: isActive ? 600 : 500,
          transition: 'background-color 0.15s, color 0.15s',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          position: 'relative',
        })}
      >
        <span
          style={{
            fontSize: 16,
            flexShrink: 0,
            width: collapsed ? 'auto' : 20,
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {icon}
          {cornerBadge != null && cornerBadge > 0 && collapsed && (
            <span
              title={badge2 && badge2 > 0 ? badge2Title : badgeTitle}
              style={{
                position: 'absolute',
                top: -6,
                right: -8,
                minWidth: 16,
                height: 16,
                borderRadius: 8,
                backgroundColor: cornerColor,
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                lineHeight: 1,
              }}
            >
              {cornerBadge > 99 ? '99+' : cornerBadge}
            </span>
          )}
        </span>
        {!collapsed && children}
        {!collapsed && ((badge != null && badge > 0) || (badge2 != null && badge2 > 0)) && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
            {badge != null && badge > 0 && (
              <span
                title={badgeTitle}
                style={{
                  minWidth: 20,
                  height: 20,
                  borderRadius: 10,
                  backgroundColor: '#e53e3e',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 6px',
                  lineHeight: 1,
                }}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            )}
            {badge2 != null && badge2 > 0 && (
              <span
                title={badge2Title}
                style={{
                  minWidth: 20,
                  height: 20,
                  borderRadius: 10,
                  backgroundColor: 'var(--color-accent)',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 6px',
                  lineHeight: 1,
                }}
              >
                {badge2 > 99 ? '99+' : badge2}
              </span>
            )}
          </span>
        )}
      </NavLink>
    </li>
  );
}
